import { useMutation } from '@apollo/client';
import { Box, Icon, IconSize, SideSheetBody, SideSheetContentCol, Text } from '@bechtel/gds';
import { UPDATE_USER_DEFAULT_PROJECT, USER_PROJECT_ID, useAppUser } from '@bechtel/ht-clients-common';
import {
  OperatorType,
  Project,
  UpdateUserDefaultProjectInput,
  UpdateUserDefaultProjectResult,
  UserProject,
} from '@bechtel/ht-types';
import { get, map } from 'lodash';
import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { ErrorBoundary } from '~/components';
import LoadingIndicator from '~/components/loading-indicator';
import { useSearchUserProject } from '../../hooks/use-search-user-projects';

//@ts-ignore ignore image import
import { client } from '~/apollo-client';
import { DrawerBackdropStyled, DrawerOuterStyled, ProjectTileStyled, SubContainerStyled } from './styled';

interface ProjectTileProp {
  project?: Project;
  selected?: boolean;
  onSelectProject: () => void;
}

interface ProjectListingProps {
  close: () => void;
}
export const ProjectTile: FC<ProjectTileProp> = ({ project, selected, onSelectProject }) => {
  return (
    <ProjectTileStyled
      onClick={selected ? () => {} : onSelectProject}
      style={{ cursor: selected ? 'no-drop' : 'pointer' }}
    >
      <Box style={{ width: '180px' }}>
        <Text size="300" weight="600" color="primary.500">
          {project?.name}
        </Text>
      </Box>
      <Box>
        <Text size="300" color="grey.600">
          {project?.jobNumber}
          {project?.subJobNumber && `-${project?.subJobNumber}`}
        </Text>
      </Box>
      <Box justifyContent={{ sm: 'flex-end' }} style={{ width: '28px' }}>
        {selected && <Icon icon="check" iconStyle="regular" size={IconSize.SMALL} color="primary.600" />}
      </Box>
    </ProjectTileStyled>
  );
};

export const ProjectListing: FC<ProjectListingProps> = ({ close }) => {
  const { appUser } = useAppUser();
  const navigate = useNavigate();
  const { searchUserProject: searchUserProject, data, loading, error } = useSearchUserProject();
  useEffect(() => {
    searchUserProject({
      sortInput: [],
      filterInput: {
        and: [
          {
            field: 'userId',
            operator: OperatorType.EQUAL,
            value: appUser?.id,
          },
        ],
      },
    });
  }, [appUser?.id, searchUserProject, appUser]);
  const allProjectList: UserProject[] = get(data, 'searchUserProjects.items', []);

  const [updateUserDefaultProject, { data: userDefaultProjectData, loading: updating }] = useMutation<
    { updateUserDefaultProject: UpdateUserDefaultProjectResult },
    { updateUserDefaultProjectInput: UpdateUserDefaultProjectInput }
  >(UPDATE_USER_DEFAULT_PROJECT, {
    onCompleted: async () => {
      window.sessionStorage.setItem(
        USER_PROJECT_ID,
        userDefaultProjectData?.updateUserDefaultProject?.user?.defaultProjectId
          ? userDefaultProjectData?.updateUserDefaultProject?.user?.defaultProjectId
          : ''
      );
      close();
      await client.resetStore();
      navigate('/', { replace: true });
    },
  });
  return (
    <DrawerBackdropStyled onClick={close}>
      <DrawerOuterStyled
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <SideSheetBody>
          <ErrorBoundary>
            <SideSheetContentCol>
              <Box flexDirection={{ sm: 'column' }}>
                <SubContainerStyled>
                  <Text color="grey.600" size="300">
                    All Projects
                  </Text>
                </SubContainerStyled>
                {(loading || updating) && <LoadingIndicator></LoadingIndicator>}
                {!loading &&
                  map(allProjectList, (item) => (
                    <ProjectTile
                      project={item.project || undefined}
                      key={item?.id}
                      selected={item?.project?.id === appUser?.defaultProject?.id}
                      onSelectProject={async () => {
                        if (item.project?.id) {
                          await updateUserDefaultProject({
                            variables: {
                              updateUserDefaultProjectInput: { projectId: item.project?.id },
                            },
                          });
                        }
                      }}
                    />
                  ))}
              </Box>
            </SideSheetContentCol>
          </ErrorBoundary>
        </SideSheetBody>
      </DrawerOuterStyled>
    </DrawerBackdropStyled>
  );
};
